import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Award,
  Crown,
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Gift,
  Repeat,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import apiClient from '@/api/client'
import { cn, formatCurrency, formatDateTimeHuman } from '@/lib/utils'
import type { APIResponse, LoyaltyConfig, LoyaltyStats } from '@/types'
import { MarketingShell } from './MarketingShell'
import { AdminCustomerProfile } from '@/components/admin/AdminCustomerProfile'
import { STAGE_BADGE, STAGE_LABEL } from '@/components/admin/AdminCustomersDirectory'

/**
 * Members — everyone enrolled in the loyalty program. Recent-activity KPIs
 * plus a balance-ranked leaderboard; clicking a row opens the shared Customer
 * Directory profile. The program rule, receipt copy, card appearance and
 * wallet setup live on the sibling "Loyalty program" tab.
 */
export function AdminLoyaltyMembers() {
  const queryClient = useQueryClient()

  // Reward goal for the leaderboard progress bars comes from the live config.
  const configQuery = useQuery<APIResponse<LoyaltyConfig>>({
    queryKey: ['loyalty-config'],
    queryFn: () => apiClient.getLoyaltyConfig(),
    staleTime: 60_000,
  })
  const statsQuery = useQuery<APIResponse<LoyaltyStats>>({
    queryKey: ['loyalty-stats'],
    queryFn: () => apiClient.getLoyaltyStats(),
    staleTime: 60_000,
  })
  // "Show all" expands the leaderboard to every customer with a balance —
  // fetched lazily (only once the owner asks) so the default view stays light.
  const [showAllBalances, setShowAllBalances] = useState(false)
  const allBalancesQuery = useQuery<APIResponse<LoyaltyStats>>({
    queryKey: ['loyalty-stats', 'all'],
    queryFn: () => apiClient.getLoyaltyStats(true),
    enabled: showAllBalances,
    staleTime: 60_000,
  })
  // Customer clicked in the Top-balances leaderboard → open the shared
  // Customer Directory profile modal (same component, fed just the id).
  const [selectedCustomerID, setSelectedCustomerID] = useState<string | null>(null)

  const stats = statsQuery.data?.data
  const totalBalances = stats?.total_balances ?? stats?.top_balances.length ?? 0
  const balanceRows =
    showAllBalances && allBalancesQuery.data?.data?.top_balances
      ? allBalancesQuery.data.data.top_balances
      : (stats?.top_balances ?? [])

  // 0 → threshold unset, so rows show the raw balance with no bar (see LeaderboardRow).
  const loyaltyGoal = configQuery.data?.data?.threshold_visits ?? 0
  const redemptionRate =
    stats && stats.punches_last_30d > 0
      ? `${((stats.redemptions_last_30d / stats.punches_last_30d) * 100).toFixed(1)}%`
      : '—'

  return (
    <MarketingShell
      title="Members"
      description="Everyone enrolled in your loyalty program, ranked by current punch balance. Tap a member to open their full profile."
    >
      <div className="space-y-6">
        {/* KPI strip — full-width, three richer metric cards so the
            section reads balanced instead of a 50/50 split with an empty
            left half. */}
        <div className="space-y-3">
          <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Recent activity · last 30 days
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            <LoyaltyKpiCard
              icon={Sparkles}
              label="Punches issued"
              value={stats?.punches_last_30d.toLocaleString() ?? '—'}
              sublabel="last 30 days"
            />
            <LoyaltyKpiCard
              icon={Gift}
              label="Rewards redeemed"
              value={stats?.redemptions_last_30d.toLocaleString() ?? '—'}
              sublabel="last 30 days"
            />
            <LoyaltyKpiCard
              icon={Repeat}
              label="Redemption rate"
              value={redemptionRate}
              sublabel="redeemed per punch"
            />
          </div>
        </div>

        {/* Top balances — columnar leaderboard. Default Top 10; "Show all"
            expands to the full balance-ranked list (the only place customers
            are ranked by punch balance). */}
        <Card className="p-4 sm:p-5">
          <div className="mb-3 flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-500" aria-hidden />
            <h4 className="text-base font-semibold text-foreground">Top balances</h4>
            {totalBalances > 0 && (
              <Badge variant="secondary" className="ml-auto text-sm">
                {showAllBalances ? `${totalBalances} total` : `Top ${Math.min(10, totalBalances)}`}
              </Badge>
            )}
          </div>

          {totalBalances === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2.5 rounded-lg border border-dashed border-border/70 px-4 py-10 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Award className="h-5 w-5" aria-hidden />
              </div>
              <p className="text-sm text-muted-foreground">
                No active balances yet — punches will appear here as guests earn them.
              </p>
            </div>
          ) : (
            <>
              {/* Column header — aligns with the cell widths in LeaderboardRow. */}
              <div className="flex items-center gap-3 border-b border-border/60 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <span className="w-7 shrink-0" aria-hidden />
                <span className="min-w-0 flex-1">Member</span>
                <span className="hidden w-24 shrink-0 text-right sm:block">Spend</span>
                <span className="hidden w-16 shrink-0 text-right md:block">Visits</span>
                <span className="hidden w-24 shrink-0 text-right lg:block">Last visit</span>
                <span className="w-32 shrink-0 text-right">Punches</span>
              </div>

              <div
                className={cn(
                  'divide-y divide-border/60',
                  showAllBalances && 'max-h-[28rem] overflow-y-auto',
                )}
              >
                {balanceRows.map((row, i) => (
                  <LeaderboardRow
                    key={row.id}
                    rank={i + 1}
                    row={row}
                    goal={loyaltyGoal}
                    onSelect={setSelectedCustomerID}
                  />
                ))}
              </div>

              {totalBalances > 10 && (
                <div className="mt-3 flex justify-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1.5 text-sm"
                    onClick={() => setShowAllBalances((v) => !v)}
                  >
                    {allBalancesQuery.isFetching && showAllBalances ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : showAllBalances ? (
                      <ChevronUp className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5" />
                    )}
                    {showAllBalances ? 'Show less' : `Show all ${totalBalances}`}
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      <AdminCustomerProfile
        customerID={selectedCustomerID}
        open={selectedCustomerID !== null}
        onOpenChange={(o) => {
          if (!o) {
            setSelectedCustomerID(null)
            // A merge/edit inside the modal can change who's on the board.
            queryClient.invalidateQueries({ queryKey: ['loyalty-stats'] })
          }
        }}
      />
    </MarketingShell>
  )
}

/** One metric card in the loyalty KPI strip — mirrors the analytics
 *  MetricTile vocabulary: small icon-flanked uppercase label, big
 *  tabular value, muted sublabel. */
function LoyaltyKpiCard({
  icon: Icon,
  label,
  value,
  sublabel,
}: {
  icon: typeof Sparkles
  label: string
  value: React.ReactNode
  sublabel: string
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider text-muted-foreground">
          <Icon className="h-3.5 w-3.5" aria-hidden />
          {label}
        </div>
        <div className="mt-2 text-2xl font-semibold tabular-nums text-foreground">{value}</div>
        <div className="mt-1 text-sm text-muted-foreground">{sublabel}</div>
      </CardContent>
    </Card>
  )
}

/** Up to two initials from a display name, uppercased; "?" when blank. */
function loyaltyInitials(name?: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return parts
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('')
}

/** One leaderboard row — rank, initials avatar, name/phone, and a
 *  progress-to-reward bar driven by the live reward goal. A member at or
 *  over the goal shows a "Reward ready" badge; when no goal is set we
 *  just show the raw balance. */
function LeaderboardRow({
  rank,
  row,
  goal,
  onSelect,
}: {
  rank: number
  row: LoyaltyStats['top_balances'][number]
  goal: number
  onSelect: (id: string) => void
}) {
  const ready = goal > 0 && row.balance >= goal
  const pct = goal > 0 ? Math.min(100, Math.round((row.balance / goal) * 100)) : 0
  const lastVisit = row.last_visit_at ? new Date(row.last_visit_at) : null

  return (
    <button
      type="button"
      onClick={() => onSelect(row.id)}
      className="flex w-full items-center gap-3 py-2.5 text-left transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus:outline-none"
    >
      {/* Rank */}
      <span className="w-7 shrink-0 text-center text-sm font-medium tabular-nums text-muted-foreground">
        {rank}
      </span>
      {/* Member */}
      <span className="flex min-w-0 flex-1 items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
          {loyaltyInitials(row.display_name)}
        </span>
        <span className="min-w-0">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-foreground">
              {row.display_name || <span className="italic text-muted-foreground">unnamed</span>}
            </span>
            {row.lifecycle_stage && (
              <Badge
                variant="outline"
                className={`shrink-0 px-1.5 py-0 text-[10px] uppercase tracking-wider ${STAGE_BADGE[row.lifecycle_stage]}`}
              >
                {STAGE_LABEL[row.lifecycle_stage]}
              </Badge>
            )}
            {row.tier && (
              <Badge
                variant="outline"
                className="shrink-0 border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] uppercase tracking-wider text-amber-700 dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-300"
                title="Loyalty tier — recomputed nightly from visit count"
              >
                {row.tier}
              </Badge>
            )}
          </span>
          <span className="block truncate font-mono text-sm text-muted-foreground">
            {row.phone_normalized || '—'}
          </span>
        </span>
      </span>
      {/* Spend */}
      <span className="hidden w-24 shrink-0 text-right text-sm tabular-nums text-foreground sm:block">
        {typeof row.lifetime_spend === 'number' ? formatCurrency(row.lifetime_spend) : '—'}
      </span>
      {/* Visits */}
      <span className="hidden w-16 shrink-0 text-right text-sm tabular-nums text-muted-foreground md:block">
        {typeof row.visit_count === 'number' ? row.visit_count : '—'}
      </span>
      {/* Last visit */}
      <span
        className="hidden w-24 shrink-0 text-right text-sm text-muted-foreground lg:block"
        title={lastVisit ? formatDateTimeHuman(lastVisit) : undefined}
      >
        {lastVisit
          ? lastVisit.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
          : '—'}
      </span>
      {/* Punches */}
      <span className="w-32 shrink-0">
        {goal <= 0 ? (
          <span className="block text-right text-sm font-medium tabular-nums text-foreground">
            {row.balance}
          </span>
        ) : ready ? (
          <span className="flex justify-end">
            <Badge className="border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-300">
              Reward ready
            </Badge>
          </span>
        ) : (
          <span className="block space-y-1">
            <Progress value={pct} className="h-1.5" />
            <span className="block text-right text-sm tabular-nums text-muted-foreground">
              {row.balance}/{goal}
            </span>
          </span>
        )}
      </span>
    </button>
  )
}
